/**
 * Vorlage „Pürieren“.
 *
 * Ein Stabmixer taucht in den Topf und wandert langsam hin und her. Die
 * Stücke werden weniger, die Oberfläche wird glatt und cremig. Danach wird
 * der Mixer herausgezogen.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { compact, faded, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { motionLines, surfaceLine } from '../parts/effects.ts';
import { cube, scatter } from '../parts/food.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { place, stickBlender } from '../parts/utensils.ts';
import { contents, pot } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const POT = { cx: 106, width: 124, height: 52 } as const;
const RIM_Y = GROUND_Y - POT.height;
const LEVEL = 0.3;

/** Inhalte: Ton der Flüssigkeit und der Stücke. */
const ITEMS = { soup: 'Suppe', sauce: 'Soße', fruit: 'Obst' } as const;
const TONES: Readonly<Record<keyof typeof ITEMS, { liquid: Tone; pieces: Tone }>> = {
  soup: { liquid: 'shade', pieces: 'tint' },
  sauce: { liquid: 'deep', pieces: 'shade' },
  fruit: { liquid: 'tint', pieces: 'paper' },
};

/** Zeitplan. */
const TIME = {
  in: [0.02, 0.14],
  blend: [0.16, 0.78],
  out: [0.8, 0.9],
  reset: [0.9, 1],
} as const;

/** Vorlage „Pürieren“. */
export const mixBlendTemplate = defineTemplate({
  id: 'mix.blend',
  group: 'mix',
  title: 'Pürieren',
  description: 'Der Stabmixer zerkleinert die Stücke im Topf, bis eine glatte, cremige Masse entsteht.',
  durationSeconds: 5,
  keywords: ['pürieren', 'püriert', 'pürierstab', 'stabmixer', 'mixen', 'fein mixen', 'im mixer', 'cremig'],
  stillTime: 0.45,
  parameters: [choice('item', 'Inhalt', ITEMS, 'soup')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('soße') || lower.includes('sauce')) return { item: 'sauce' };
    if (/obst|frucht|früchte|beeren|smoothie|mus\b/.test(lower)) return { item: 'fruit' };
    if (lower.includes('suppe')) return { item: 'soup' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'soup') as keyof typeof ITEMS;
    const tones = TONES[item] ?? TONES.soup;
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const blendPhase = segment(t, ...TIME.blend);
    const smooth = ease.inOutSine(blendPhase) * (1 - reset);
    const running = blendPhase > 0 && blendPhase < 1;
    const dip = ease.inOutCubic(segment(t, ...TIME.in)) - ease.inOutCubic(segment(t, ...TIME.out));

    const surfaceY = RIM_Y + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);
    // Stücke verschwinden nacheinander, die größten zuletzt.
    const cubes = scatter(POT.cx, surfaceY, rx * 0.72, ry * 0.55, 11, 17);
    const pieces: SceneNode[] = cubes.flatMap((position, index) => {
      const gone = segment(smooth, (index / cubes.length) * 0.8, (index / cubes.length) * 0.8 + 0.2);
      return gone >= 1 ? [] : [cube(position, lerp(7, 2, gone), tones.pieces, STROKE.fine)];
    });
    const swirl = running ? 1.6 : 0.4;
    const liquid = contents(vessel, LEVEL, tones.liquid, [
      shape(surfaceLine(POT.cx - rx, surfaceY - ry * 0.15, rx * 2, swirl, t * 4, 3), {
        stroke: 'deep',
        strokeWidth: STROKE.fine,
      }),
      ...pieces,
    ]);

    const wander = running ? Math.sin(blendPhase * Math.PI * 6) * 22 : 0;
    // Der Mixer ist hoch; er senkt sich nur ein Stück, damit er im Bild bleibt.
    const tipY = lerp(surfaceY - 4, surfaceY + 14, dip);
    const blender = faded(Math.min(1, dip * 3 + (running ? 1 : 0)), [
      place(POT.cx + wander, tipY, 0, stickBlender(running), 0.85),
    ]);
    return compact([
      ...counter(),
      shadow(POT.cx, POT.width * 0.48),
      ...vessel.back,
      liquid,
      blender,
      ...vessel.front,
      running ? motionLines(POT.cx + wander + 20, RIM_Y - 30, -20, 8, 3, 5) : null,
      running ? motionLines(POT.cx + wander - 20, RIM_Y - 30, 200, 8, 3, 5) : null,
    ]);
  },
  hints() {
    return [
      arrow([
        { x: POT.cx - 34, y: RIM_Y - 12 },
        { x: POT.cx + 34, y: RIM_Y - 12 },
      ]),
      arrow([
        { x: POT.cx + 34, y: RIM_Y - 22 },
        { x: POT.cx - 34, y: RIM_Y - 22 },
      ]),
    ];
  },
});
